<template>
  <div class="es-root" :class="{ 'is-open': open, 'is-disabled': disabled }">
    <div class="es-toggle" @mousedown.prevent="toggle" ref="toggle">
      <span v-if="displayLabel" class="es-value">{{ displayLabel }}</span>
      <span v-else class="es-placeholder">{{ placeholder }}</span>
      <div class="es-actions">
        <button
          v-if="clearable && currentValue != null"
          class="es-clear"
          @mousedown.stop="clear"
          tabindex="-1"
        >
          <i class="fa fa-times"></i>
        </button>
        <i class="fa fa-chevron-down es-arrow"></i>
      </div>
    </div>

    <portal to="editor-select-portal">
      <transition name="es-drop">
        <div
          v-if="open"
          class="es-dropdown-portal"
          :style="dropdownStyle"
          @mousedown.stop
        >
          <div class="es-dropdown">
            <div v-if="searchable" class="es-search-wrap">
              <input
                ref="search"
                v-model="query"
                class="es-search"
                placeholder="Search…"
                @keydown.esc="close"
                @keydown.down.prevent="moveHighlight(1)"
                @keydown.up.prevent="moveHighlight(-1)"
                @keydown.enter.prevent="selectHighlighted"
              />
            </div>

            <div class="es-options" ref="options">
              <div
                v-for="(opt, idx) in filteredOptions"
                :key="getOptionKey(opt, idx)"
                class="es-option"
                :class="{
                  'is-highlighted': idx === highlightedIndex,
                  'is-selected': isSelected(opt),
                }"
                @mouseenter="highlightedIndex = idx"
                @click.prevent="selectOption(opt)"
              >
                {{ getOptionLabel(opt) }}
              </div>
              <div v-if="filteredOptions.length === 0" class="es-no-options">
                No options
              </div>
            </div>
          </div>
        </div>
      </transition>
    </portal>
  </div>
</template>

<script>
export default {
  name: "EditorSelect",
  props: {
    value: { default: null },
    options: { type: Array, default: () => [] },
    label: { type: String, default: null },
    reduce: { type: Function, default: null },
    placeholder: { type: String, default: "Select…" },
    searchable: { type: Boolean, default: true },
    clearable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      open: false,
      query: "",
      highlightedIndex: 0,
      dropdownStyle: {},
    };
  },
  computed: {
    currentValue() {
      return this.value;
    },
    filteredOptions() {
      if (!this.query) return this.options;
      const q = this.query.toLowerCase();
      return this.options.filter((opt) =>
        this.getOptionLabel(opt).toLowerCase().includes(q)
      );
    },
    selectedOption() {
      if (this.currentValue == null) return null;
      return this.options.find((opt) => this.getReducedValue(opt) === this.currentValue);
    },
    displayLabel() {
      if (this.selectedOption == null) return "";
      return this.getOptionLabel(this.selectedOption);
    },
  },
  methods: {
    getOptionLabel(opt) {
      if (opt == null) return "";
      if (typeof opt === "string" || typeof opt === "number") return String(opt);
      if (this.label && opt[this.label] != null) return String(opt[this.label]);
      return String(opt);
    },
    getReducedValue(opt) {
      if (this.reduce) return this.reduce(opt);
      return opt;
    },
    getOptionKey(opt, idx) {
      const val = this.getReducedValue(opt);
      return val != null ? val : idx;
    },
    isSelected(opt) {
      return this.getReducedValue(opt) === this.currentValue;
    },
    toggle() {
      if (this.disabled) return;
      this.open ? this.close() : this.openDropdown();
    },
    getPortalEl(selector) {
      const targets = document.querySelectorAll(".es-dropdown-portal");
      for (const t of targets) {
        if (t._esOwner === this._uid) return selector ? t.querySelector(selector) : t;
      }
      return null;
    },
    tagPortal() {
      this.$nextTick(() => {
        const targets = document.querySelectorAll(".es-dropdown-portal");
        for (const t of targets) {
          if (!t._esOwner) { t._esOwner = this._uid; break; }
        }
      });
    },
    openDropdown() {
      this.open = true;
      this.query = "";
      this.highlightedIndex = this.selectedOption
        ? this.filteredOptions.indexOf(this.selectedOption)
        : 0;
      if (this.highlightedIndex < 0) this.highlightedIndex = 0;
      this.updatePosition();
      this.tagPortal();
      this.$nextTick(() => {
        const search = this.getPortalEl(".es-search");
        if (this.searchable && search) search.focus();
        this.scrollToHighlighted();
      });
      this._onDocClick = (e) => {
        if (!this.$el.contains(e.target)) this.close();
      };
      this._onScroll = () => this.updatePosition();
      setTimeout(() => {
        document.addEventListener("mousedown", this._onDocClick);
        window.addEventListener("scroll", this._onScroll, true);
        window.addEventListener("resize", this._onScroll);
      }, 0);
    },
    close() {
      if (!this.open) return;
      this.open = false;
      this.query = "";
      this.cleanup();
    },
    cleanup() {
      if (this._onDocClick) {
        document.removeEventListener("mousedown", this._onDocClick);
        this._onDocClick = null;
      }
      if (this._onScroll) {
        window.removeEventListener("scroll", this._onScroll, true);
        window.removeEventListener("resize", this._onScroll);
        this._onScroll = null;
      }
    },
    updatePosition() {
      const toggle = this.$refs.toggle;
      if (!toggle) return;
      const rect = toggle.getBoundingClientRect();
      this.dropdownStyle = {
        position: "fixed",
        top: rect.bottom + "px",
        left: rect.left + "px",
        width: rect.width + "px",
        zIndex: 999999,
      };
    },
    selectOption(opt) {
      const val = this.getReducedValue(opt);
      this.$emit("input", val);
      this.$emit("option:selected", opt);
      this.close();
    },
    clear() {
      this.$emit("input", null);
      this.$emit("option:selected", null);
    },
    moveHighlight(dir) {
      const len = this.filteredOptions.length;
      if (!len) return;
      this.highlightedIndex = (this.highlightedIndex + dir + len) % len;
      this.scrollToHighlighted();
    },
    selectHighlighted() {
      if (this.filteredOptions[this.highlightedIndex]) {
        this.selectOption(this.filteredOptions[this.highlightedIndex]);
      }
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const container = this.$refs.options;
        if (!container) return;
        const el = container.children[this.highlightedIndex];
        if (el) el.scrollIntoView({ block: "nearest" });
      });
    },
  },
  beforeDestroy() {
    this.cleanup();
  },
};
</script>

<style lang="scss">
$es-bg-toggle:      #1e2130;
$es-bg-dropdown:    #252836;
$es-bg-hover:       #2e3348;
$es-bg-selected:    rgba(91, 124, 246, 0.12);
$es-border:         #3a3f55;
$es-border-focus:   #5b7cf6;
$es-text-primary:   #e8eaf2;
$es-text-secondary: #b0b8d0;
$es-text-muted:     #6b7394;
$es-text-placeholder: #555d7a;
$es-accent:         #5b7cf6;
$es-radius:         6px;

.es-dropdown-portal {
  .es-dropdown {
    background: $es-bg-dropdown;
    border: 1px solid $es-border-focus;
    border-top: none;
    border-radius: 0 0 $es-radius $es-radius;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.4);
    overflow: hidden;
  }

  .es-search-wrap {
    padding: 6px 8px;
    border-bottom: 1px solid rgba($es-border, 0.6);
  }

  .es-search {
    width: 100%;
    height: 30px;
    padding: 0 10px;
    background: $es-bg-toggle;
    border: 1px solid $es-border;
    border-radius: 4px;
    color: $es-text-primary;
    font-size: 12px;
    outline: none;
    transition: border-color 0.15s;
    box-sizing: border-box;

    &::placeholder { color: $es-text-placeholder; }
    &:focus { border-color: $es-border-focus; }
  }

  .es-options {
    max-height: 200px;
    overflow-y: auto;
    padding: 4px;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: $es-border; border-radius: 2px; }
  }

  .es-option {
    padding: 7px 10px;
    border-radius: 4px;
    color: $es-text-secondary;
    cursor: pointer;
    transition: background 0.1s, color 0.1s;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 13px;

    &.is-highlighted { background: $es-bg-hover; color: $es-text-primary; }
    &.is-selected { color: $es-accent; font-weight: 500; }
    &.is-highlighted.is-selected { background: $es-bg-selected; }
  }

  .es-no-options {
    padding: 12px;
    text-align: center;
    color: $es-text-muted;
    font-size: 12px;
  }

  .es-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba($es-text-muted, 0.2);
    border-left-color: $es-accent;
    border-radius: 50%;
    animation: es-spin 0.8s linear infinite;
    display: inline-block;

    &--small { width: 12px; height: 12px; }
  }

  .es-loading {
    padding: 12px;
    text-align: center;
    color: $es-text-muted;
    font-size: 12px;
  }

  .es-load-more {
    padding: 6px;
    display: flex;
    justify-content: center;
  }

  @keyframes es-spin {
    to { transform: rotate(360deg); }
  }
}

.es-drop-enter-active,
.es-drop-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
  transform-origin: top center;
}
.es-drop-enter,
.es-drop-leave-to {
  opacity: 0;
  transform: scaleY(0.9);
}
</style>

<style scoped lang="scss">
$bg-toggle:      #1e2130;
$border:         #3a3f55;
$border-focus:   #5b7cf6;
$text-primary:   #e8eaf2;
$text-muted:     #6b7394;
$text-placeholder: #555d7a;
$radius:         6px;
$danger:         #f87171;

.es-root {
  position: relative;
  width: 100%;
  font-size: 13px;
}

.es-toggle {
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: 0 8px 0 12px;
  background: $bg-toggle;
  border: 1px solid $border;
  border-radius: $radius;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  gap: 8px;

  .is-open & {
    border-color: $border-focus;
    box-shadow: 0 0 0 2px rgba($border-focus, 0.15);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .is-disabled & {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.es-value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: $text-primary;
}

.es-placeholder {
  flex: 1;
  color: $text-placeholder;
}

.es-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.es-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 3px;
  background: transparent;
  color: $text-muted;
  font-size: 10px;
  cursor: pointer;
  transition: color 0.12s, background 0.12s;

  &:hover { color: $danger; background: rgba($danger, 0.1); }
}

.es-arrow {
  font-size: 10px;
  color: $text-muted;
  transition: transform 0.18s ease;

  .is-open & { transform: rotate(180deg); }
}
</style>
